import React, { useState, useEffect } from 'react';
import { bookings } from '../../api';
import CreateRecurringModal from './CreateRecurringModal';
import RecurringPatternsList from './RecurringPatternsList';
import CreateCoachingPlanModal from './CreateCoachingPlanModal';
import CoachingPlansList from './CoachingPlansList';
import useBufferSync from './useBufferSync';
import BufferSyncReport from './BufferSyncReport';
import SlotCourtInfo from './SlotCourtInfo';
import AdvertiseSlotsModal from './AdvertiseSlotsModal';
import './Bookings.css';

export default function MyAvailableSlotsTab({ students = [], syncBufferLessons, navTarget = null }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showAdvertiseModal, setShowAdvertiseModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    plans: false,
    recurring: false,
    slots: false
  });

  // Shown on the collapsed "Coaching Plans" button so the coach can see how
  // many plans are active/available without expanding the section - fetched
  // independently of CoachingPlansList (which only mounts once expanded).
  const [planCount, setPlanCount] = useState(null);

  // ✅ NEW: Edit modal state
  const [editingSlotId, setEditingSlotId] = useState(null);
  const [editForm, setEditForm] = useState({
    maxBookings: 1,
    sessionType: 'custom',
    tags: []
  });

  useEffect(() => {
    loadSlots();
  }, []);

  // Calendar buffer sync (moved here from the Bookings tab bar) - reloads the
  // slot list itself once the sync finishes.
  const { isSyncingBuffers, syncStatus, syncMessage, runSync, report, closeReport } = useBufferSync(
    syncBufferLessons,
    () => loadSlots()
  );

  useEffect(() => {
    loadPlanCount();
  }, [refreshKey]);

  const loadPlanCount = async () => {
    try {
      const response = await bookings.getCoachingPlans();
      const plans = response.data || [];
      const count = plans.filter(p => ['available', 'booked', 'in-progress'].includes(p.bookingStatus)).length;
      setPlanCount(count);
    } catch (err) {
      console.error('❌ Error loading plan count:', err);
    }
  };

  // Notification tap about plans / recurring patterns / slots: expand that
  // section and bring it into view
  useEffect(() => {
    if (!navTarget || navTarget.tab !== 'bookings') return undefined;
    const section = navTarget.section;
    if (!['plans', 'recurring', 'slots'].includes(section)) return undefined;
    setExpandedSections(prev => ({ ...prev, [section]: true }));
    const timer = setTimeout(() => {
      const el = document.getElementById(`booking-section-${section}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('note-focus-flash');
        setTimeout(() => el.classList.remove('note-focus-flash'), 3500);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [navTarget]);

  // ⭐ Feature / un-feature a slot in the students' Coaching-tab banner
  const handleToggleFeature = async (slot) => {
    const turningOn = !slot.featured;
    let message = '';
    if (turningOn) {
      const typed = window.prompt('Optional message for your students (max 140 characters):', '');
      if (typed === null) return; // cancelled
      message = typed.slice(0, 140);
    }
    try {
      await bookings.featureSlot(slot._id, turningOn, message);
      setSlots(prev => prev.map(s => s._id === slot._id
        ? { ...s, featured: turningOn, featuredMessage: turningOn ? (message || null) : null }
        : s));
    } catch (err) {
      alert(err.response?.data?.message || 'Could not update this slot');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const loadSlots = async () => {
    try {
      setLoading(true);
      const response = await bookings.getMySlots();
      setSlots(response.data.slots || []);
      setError(null);
    } catch (err) {
      console.error('❌ Error loading slots:', err);
      setError('Failed to load your coaching slots');
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Open edit modal for a slot
  const handleOpenEditModal = (slot) => {
    setEditingSlotId(slot._id);
    setEditForm({
      maxBookings: slot.maxBookings || 1,
      sessionType: slot.sessionType || 'custom',
      tags: slot.tags || []
    });
  };

  // ✅ NEW: Close edit modal
  const handleCloseEditModal = () => {
    setEditingSlotId(null);
    setEditForm({
      maxBookings: 1,
      sessionType: 'custom',
      tags: []
    });
  };

  // ✅ NEW: Save edited slot
  const handleSaveSlotEdits = async () => {
    if (!editingSlotId) return;

    try {
      const updateData = {
        maxBookings: parseInt(editForm.maxBookings),
        sessionType: editForm.sessionType,
        tags: editForm.tags
      };

      await bookings.updateSlot(editingSlotId, updateData);
      alert('✅ Slot updated successfully!');
      handleCloseEditModal();
      loadSlots(); // Reload to show updates
    } catch (err) {
      console.error('❌ Error updating slot:', err);
      alert(err.response?.data?.message || 'Failed to update slot');
    }
  };

  // ✅ NEW: Handle tag input (comma-separated)
  const handleTagsChange = (tagString) => {
    const tags = tagString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    setEditForm(prev => ({ ...prev, tags }));
  };

  const handleDeleteSlot = async (slotId) => {
    const slot = slots.find(s => s._id === slotId);
    if (!slot) return;

    // Two-step deletion flow
    let confirmMessage;
    if (slot.status === 'archived') {
      confirmMessage = '⚠️ Permanently delete this slot? This cannot be undone.';
    } else {
      confirmMessage = '📋 Archive this coaching slot? It will no longer appear for booking. Click delete again to permanently remove.';
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await bookings.deleteSlot(slotId);

      // Check if permanently deleted
      if (response.data.deleted) {
        // Permanently deleted - remove from list
        setSlots(slots.filter(s => s._id !== slotId));
        alert('✅ Coaching slot permanently deleted');
      } else {
        // Archived - update slot status in list
        setSlots(slots.map(s =>
          s._id === slotId ? { ...s, status: 'archived' } : s
        ));
        alert('📋 Slot archived. Click delete again to permanently remove.');
      }
    } catch (err) {
      console.error('❌ Error deleting slot:', err);
      alert('Failed to delete slot');
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      meridiem: 'short'
    });
  };

  const getAvailableCount = (slot) => {
    return Math.max(0, slot.maxBookings - slot.currentBookings);
  };

  const getStatusColor = (slot) => {
    if (slot.status === 'archived') return '#999';
    if (slot.status === 'full') return '#ef4444';
    return '#22c55e';
  };

  if (loading) {
    return (
      <div className="booking-tab-content">
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          ⏳ Loading your coaching slots...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-tab-content">
        <div style={{ textAlign: 'center', padding: '40px', color: '#e74c3c' }}>
          ⚠️ {error}
          <br />
          <button
            onClick={loadSlots}
            style={{
              marginTop: '15px',
              padding: '8px 16px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  if (slots.length === 0) {
    return (
      <div className="booking-tab-content">
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          <div style={{ fontSize: '48px', marginBottom: '15px' }}>📭</div>
          No coaching slots offered yet
          <br />
          <span style={{ fontSize: '14px' }}>Create a recurring pattern or add a "buffer" event to your calendar to create slots</span>
        </div>
      </div>
    );
  }

  const handleRecurringSuccess = () => {
    setRefreshKey(prev => prev + 1);
    loadSlots();
  };

  const handlePlanSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleRemovePlayer = async (slotId, bookingId, playerName) => {
    if (!window.confirm(`Remove ${playerName} from this coaching slot? They will be notified.`)) {
      return;
    }

    try {
      await bookings.removePlayerFromSlot(bookingId);
      // Reload slots to update UI
      loadSlots();
      alert(`✅ ${playerName} has been removed from the slot.`);
    } catch (err) {
      console.error('❌ Error removing player:', err);
      alert(err.response?.data?.message || 'Failed to remove player');
    }
  };

  return (
    <div className="booking-tab-content">
      <CreateRecurringModal
        isOpen={showRecurringModal}
        onClose={() => setShowRecurringModal(false)}
        onSuccess={handleRecurringSuccess}
      />

      <AdvertiseSlotsModal
        isOpen={showAdvertiseModal}
        onClose={() => setShowAdvertiseModal(false)}
        slots={slots}
        students={students}
        onDone={loadSlots}
      />

      <CreateCoachingPlanModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        onSuccess={handlePlanSuccess}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <h3 style={{ margin: 0, color: '#2c3e50' }}>
          📅 Your Coaching Availability
        </h3>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowRecurringModal(true)}
            style={{
              padding: '8px 16px',
              background: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '13px'
            }}
          >
            🔄 Create Recurring Pattern
          </button>
          <button
            onClick={() => setShowPlanModal(true)}
            style={{
              padding: '8px 16px',
              background: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '13px'
            }}
          >
            📦 Create Coaching Plan
          </button>
          <button
            onClick={() => setShowAdvertiseModal(true)}
            style={{
              padding: '8px 16px',
              background: '#e67e22',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '13px'
            }}
          >
            📣 Advertise Slots
          </button>
          <button
            onClick={runSync}
            disabled={isSyncingBuffers || !syncBufferLessons}
            title={isSyncingBuffers ? 'Syncing...' : 'Sync calendar buffers: creates, updates, and removes slots as needed, and cleans up orphaned or duplicate slots'}
            style={{
              padding: '8px 16px',
              background: isSyncingBuffers
                ? '#95a5a6'
                : syncStatus === 'success'
                ? '#27ae60'
                : syncStatus === 'error'
                ? '#e74c3c'
                : '#8e44ad',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isSyncingBuffers ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              fontSize: '13px',
              opacity: isSyncingBuffers ? 0.75 : 1,
              transition: 'background 0.2s ease'
            }}
          >
            <span style={{ display: 'inline-block', animation: isSyncingBuffers ? 'spin 1s linear infinite' : 'none' }}>
              {isSyncingBuffers ? '⏳' : syncStatus === 'success' ? '✅' : syncStatus === 'error' ? '❌' : '📅'}
            </span>{' '}
            {isSyncingBuffers ? 'Syncing...' : 'Sync Buffers'}
          </button>
        </div>
      </div>

      {report && <BufferSyncReport report={report} onClose={closeReport} />}

      {syncMessage && (
        <div style={{
          padding: '12px 16px',
          background: syncStatus === 'success' ? '#d4edda' : syncStatus === 'error' ? '#fadbd8' : '#fff3cd',
          border: `1px solid ${syncStatus === 'success' ? '#c3e6cb' : syncStatus === 'error' ? '#f5c6cb' : '#ffeaa7'}`,
          borderRadius: '6px',
          color: syncStatus === 'success' ? '#155724' : syncStatus === 'error' ? '#721c24' : '#856404',
          fontSize: '13px',
          fontWeight: '500',
          marginBottom: '16px'
        }}>
          {syncMessage}
        </div>
      )}

      {/* COACHING PLANS SECTION */}
      <div style={{ marginBottom: '12px' }}>
        <button
          id="booking-section-plans"
          onClick={() => toggleSection('plans')}
          style={{
            width: '100%',
            padding: '12px 15px',
            background: expandedSections.plans ? '#e6f7ed' : '#f8f9fa',
            border: `1px solid ${expandedSections.plans ? '#27ae60' : '#ddd'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: expandedSections.plans ? '#155724' : '#2c3e50',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.8'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📦 Coaching Plans</span>
            {planCount !== null && (
              <span style={{
                background: expandedSections.plans ? '#27ae60' : '#3498db',
                color: 'white',
                borderRadius: '10px',
                padding: '2px 9px',
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {planCount} active
              </span>
            )}
          </span>
          <span style={{ fontSize: '16px' }}>
            {expandedSections.plans ? '▼' : '▶'}
          </span>
        </button>
        {expandedSections.plans && (
          <div style={{ marginTop: '12px' }}>
            <CoachingPlansList key={refreshKey} students={students} onPlansChanged={loadPlanCount} />
          </div>
        )}
      </div>

      {/* RECURRING PATTERNS SECTION */}
      <div style={{ marginBottom: '12px' }}>
        <button
          id="booking-section-recurring"
          onClick={() => toggleSection('recurring')}
          style={{
            width: '100%',
            padding: '12px 15px',
            background: expandedSections.recurring ? '#e3f2fd' : '#f8f9fa',
            border: `1px solid ${expandedSections.recurring ? '#1976d2' : '#ddd'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: expandedSections.recurring ? '#1565c0' : '#2c3e50',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.8'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
        >
          <span>🔁 Recurring Patterns</span>
          <span style={{ fontSize: '16px' }}>
            {expandedSections.recurring ? '▼' : '▶'}
          </span>
        </button>
        {expandedSections.recurring && (
          <div style={{ marginTop: '12px' }}>
            <RecurringPatternsList key={refreshKey} />
          </div>
        )}
      </div>

      {/* INDIVIDUAL SLOTS SECTION */}
      <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'stretch' }}>
        <button
          id="booking-section-slots"
          onClick={() => toggleSection('slots')}
          style={{
            flex: 1,
            padding: '12px 15px',
            background: expandedSections.slots ? '#fff3cd' : '#f8f9fa',
            border: `1px solid ${expandedSections.slots ? '#ff9800' : '#ddd'}`,
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 'bold',
            color: expandedSections.slots ? '#856404' : '#2c3e50',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '0.8'}
          onMouseLeave={(e) => e.target.style.opacity = '1'}
        >
          <span>📅 Individual Slots ({slots.length})</span>
          <span style={{ fontSize: '16px' }}>
            {expandedSections.slots ? '▼' : '▶'}
          </span>
        </button>
        {/* Lightweight refresh - just re-fetches this coach's own slots
            from the DB, unlike Sync Buffers/Clean Up Orphaned Slots which
            also hit Google Calendar and take noticeably longer. For when
            you just want to confirm the list reflects the latest state
            (e.g. after a player books, or a cleanup run elsewhere). */}
        <button
          onClick={loadSlots}
          disabled={loading}
          title="Refresh slot list"
          style={{
            flexShrink: 0,
            width: '44px',
            padding: '12px',
            background: '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            opacity: loading ? 0.6 : 1,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#eef1ff'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#f8f9fa'; }}
        >
          <span style={{ display: 'inline-block', animation: loading ? 'spin 1s linear infinite' : 'none' }}>🔄</span>
        </button>
      </div>

      {expandedSections.slots && (
      <div className="booking-slots-list">
        {slots.map(slot => (
          <div
            key={slot._id}
            className="booking-slot-card"
            style={{
              borderLeft: `4px solid ${getStatusColor(slot)}`,
              opacity: slot.status === 'archived' ? 0.6 : 1
            }}
          >
            <div className="booking-slot-header">
              <div style={{ flex: 1 }}>
                <h4 style={{ margin: '0 0 5px 0', color: '#2c3e50' }}>
                  {slot.title}
                </h4>
                <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                  🕐 {formatTime(slot.startTime)}
                </div>
              </div>

              <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span
                    style={{
                      padding: '4px 10px',
                      borderRadius: '999px',
                      fontSize: '10.5px',
                      fontWeight: '700',
                      background:
                        slot.status === 'full' ? '#ffe6e6' :
                        slot.status === 'archived' ? '#f0f0f0' :
                        '#e6f7ed',
                      color:
                        slot.status === 'full' ? '#c0392b' :
                        slot.status === 'archived' ? '#666' :
                        '#27ae60',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {slot.status === 'full' ? '🔴 Full' :
                     slot.status === 'archived' ? '🗑️ Archived' :
                     '🟢 Available'}
                  </span>
                </div>

                <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#2c3e50' }}>
                  {getAvailableCount(slot)}/{slot.maxBookings} open
                </span>
              </div>
            </div>

            <SlotCourtInfo slot={slot} margin="8px 0" />

            {slot.tags && slot.tags.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                {slot.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      fontSize: '11px',
                      padding: '3px 10px',
                      background: '#ecf0f1',
                      borderRadius: '999px',
                      color: '#34495e'
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div style={{ marginTop: '12px', display: 'flex', gap: '8px', fontSize: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setExpandedSlot(expandedSlot === slot._id ? null : slot._id)}
                style={{
                  flex: 1,
                  minWidth: '80px',
                  padding: '6px',
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {expandedSlot === slot._id ? '▼' : '▶'} Details
              </button>
              {slot.status === 'available' && !(slot.currentBookings > 0) && (
                <button
                  onClick={() => handleToggleFeature(slot)}
                  title={slot.featured ? 'Featured in your students\' banner - tap to remove' : 'Feature this slot in your students\' banner'}
                  style={{
                    padding: '6px 12px',
                    background: slot.featured ? '#f39c12' : '#f4f6fa',
                    color: slot.featured ? 'white' : '#7f8c8d',
                    border: slot.featured ? 'none' : '1px solid #dfe3ee',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  {slot.featured ? '⭐ Featured' : '☆ Feature'}
                </button>
              )}
              <button
                onClick={() => handleOpenEditModal(slot)}
                style={{
                  padding: '6px 12px',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✏️ Edit
              </button>
              <button
                onClick={() => handleDeleteSlot(slot._id)}
                style={{
                  padding: '6px 12px',
                  background: slot.status === 'archived' ? '#c0392b' : '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: slot.status === 'archived' ? 'bold' : 'normal'
                }}
              >
                {slot.status === 'archived' ? '🗑️ Permanently Delete' : '📋 Archive'}
              </button>
            </div>

            {expandedSlot === slot._id && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: '#f8f9fa',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#2c3e50'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Start:</strong> {new Date(slot.startTime).toLocaleString()}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>End:</strong> {new Date(slot.endTime).toLocaleString()}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Duration:</strong> {Math.round((new Date(slot.endTime) - new Date(slot.startTime)) / 60000)} minutes
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <strong>Bookings:</strong> {slot.currentBookings}/{slot.maxBookings}
                </div>

                {/* Show who has booked this slot */}
                {slot.bookings && slot.bookings.length > 0 ? (
                  <div style={{ borderTop: '1px solid #ddd', paddingTop: '12px', marginTop: '12px' }}>
                    <strong>👥 Booked by:</strong>
                    <div style={{ marginTop: '8px' }}>
                      {slot.bookings.map((booking, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: '8px',
                            marginBottom: '6px',
                            background: booking.status === 'approved' ? '#e6f7ed' : '#fff3cd',
                            border: `1px solid ${booking.status === 'approved' ? '#27ae60' : '#ff9800'}`,
                            borderRadius: '4px',
                            fontSize: '12px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>
                                {booking.status === 'approved' ? '✅' : '⏳'} {booking.playerName}
                              </div>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                📧 {booking.playerEmail}
                              </div>
                              <div style={{ fontSize: '11px', color: '#999', marginTop: '3px' }}>
                                Status: {booking.status === 'approved' ? 'Approved' : 'Pending Approval'}
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemovePlayer(slot._id, booking.bookingId, booking.playerName)}
                              style={{
                                padding: '4px 8px',
                                background: '#e74c3c',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                marginLeft: '8px',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}
                              title="Remove this player from the slot"
                            >
                              🗑️ Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ borderTop: '1px solid #ddd', paddingTop: '12px', marginTop: '12px', color: '#999', fontStyle: 'italic' }}>
                    No bookings yet
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {/* ✅ EDIT MODAL OVERLAY */}
      {editingSlotId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={handleCloseEditModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50' }}>
              ✏️ Edit Coaching Slot
            </h3>

            {/* Max Bookings - Tap to Adjust */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#2c3e50',
                marginBottom: '10px'
              }}>
                Max Participants
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                justifyContent: 'center'
              }}>
                <button
                  onClick={() => setEditForm(prev => ({
                    ...prev,
                    maxBookings: Math.max(1, prev.maxBookings - 1)
                  }))}
                  style={{
                    width: '44px',
                    height: '44px',
                    fontSize: '20px',
                    border: '2px solid #3498db',
                    background: '#3498db',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  title="Decrease"
                >
                  −
                </button>

                <div style={{
                  minWidth: '60px',
                  textAlign: 'center',
                  fontSize: '32px',
                  fontWeight: 'bold',
                  color: '#2c3e50'
                }}>
                  {editForm.maxBookings}
                </div>

                <button
                  onClick={() => setEditForm(prev => ({
                    ...prev,
                    maxBookings: Math.min(10, prev.maxBookings + 1)
                  }))}
                  style={{
                    width: '44px',
                    height: '44px',
                    fontSize: '20px',
                    border: '2px solid #27ae60',
                    background: '#27ae60',
                    color: 'white',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s'
                  }}
                  title="Increase"
                >
                  +
                </button>
              </div>
              <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '10px', textAlign: 'center' }}>
                Tap to adjust (1-10 players)
              </div>
            </div>

            {/* Session Type Dropdown */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#2c3e50',
                marginBottom: '6px'
              }}>
                Session Type
              </label>
              <select
                value={editForm.sessionType}
                onChange={(e) => setEditForm(prev => ({
                  ...prev,
                  sessionType: e.target.value
                }))}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '13px',
                  border: '1px solid #bdc3c7',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  cursor: 'pointer'
                }}
              >
                <option value="technique">🎾 Technique Training</option>
                <option value="match-play">🏆 Match Play</option>
                <option value="drill-practice">🎯 Drill Practice</option>
                <option value="match-prep">📋 Match Prep</option>
                <option value="mental">🧠 Mental Coaching</option>
                <option value="custom">⚙️ Custom</option>
              </select>
            </div>

            {/* Tags Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#2c3e50',
                marginBottom: '6px'
              }}>
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={editForm.tags.join(', ')}
                onChange={(e) => handleTagsChange(e.target.value)}
                placeholder="e.g., forehand, service, advanced"
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '13px',
                  border: '1px solid #bdc3c7',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ fontSize: '11px', color: '#7f8c8d', marginTop: '4px' }}>
                Add tags to help players find this session (optional)
              </div>
              {editForm.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {editForm.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: '11px',
                        padding: '4px 8px',
                        background: '#3498db',
                        color: 'white',
                        borderRadius: '3px',
                        display: 'inline-block'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleCloseEditModal}
                style={{
                  padding: '8px 16px',
                  background: '#ecf0f1',
                  color: '#2c3e50',
                  border: '1px solid #bdc3c7',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px'
                }}
              >
                ❌ Cancel
              </button>
              <button
                onClick={handleSaveSlotEdits}
                style={{
                  padding: '8px 16px',
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  fontSize: '13px'
                }}
              >
                ✅ Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
